import { z } from 'zod';
import { EntityNode } from './entities.js';
import { Fact } from './ask.js';
import { SourceRef } from './ask.js';
/**
 * Side-by-side entity comparison (product spec §6.5 / Foundry-style
 * "compare two entities"). Powers `GET /api/compare?a=X&b=Y` — pulls
 * each entity's facts + sources in parallel, then computes the shared
 * surface (1-hop neighbors and source documents that mention both)
 * so the analyst can see overlap at a glance.
 *
 * The shared surface is the actual analytical value: two entity
 * dossiers next to each other are useful, but the answer to "are
 * these the same actor" or "what do they have in common" lives in
 * the `shared` block.
 */
/**
 * Compare endpoint hands back labels straight from Neo4j, which carry a
 * broader vocabulary than the curated `EntityLabel` enum (the demo
 * corpus is hand-curated; the live graph's labels can include anything
 * the ingest pipeline writes). Loosen `label` to `string` for this
 * response so the wire shape is honest and the server doesn't need
 * `as never` casts.
 */
const CompareEntityNode = EntityNode.extend({ label: z.string() });
export const ComparePane = z.object({
    entity: CompareEntityNode,
    facts: z.array(Fact),
    sources: z.array(SourceRef),
});
/** Neighbor reachable from BOTH entities in 1 hop. */
export const SharedNeighbor = z.object({
    name: z.string().min(1),
    label: z.string().nullable(),
    /** Edge type on the A→neighbor edge. */
    predicateFromA: z.string(),
    /** Edge type on the B→neighbor edge. */
    predicateFromB: z.string(),
});
export const CompareResponse = z.object({
    a: ComparePane,
    b: ComparePane,
    shared: z.object({
        neighbors: z.array(SharedNeighbor),
        sources: z.array(SourceRef),
    }),
});
